import { Part } from "./contractB";

export type NodeStatus = "open" | "closed";

export interface StreamNode {
  id: string;
  tag: string;
  parent: string | null;
  attrs: Record<string, unknown>;
  text: string;
  payload?: unknown;
  atomic: boolean;
  childIds: string[];
  status: NodeStatus;
}

export type StreamStatus = "idle" | "streaming" | "done" | "error";

export interface StreamError {
  message: string;
  code: number | null;
}

export interface StreamState {
  byId: Record<string, StreamNode>;
  rootIds: string[];
  replyId: string | null;
  kind: string | null;
  version: number | null;
  status: StreamStatus;
  error: StreamError | null;
  lastSeq: number;
  seqGap: boolean;
}

export const REPLY_TAG = "zv:reply";

export function initStreamState(): StreamState {
  return {
    byId: {},
    rootIds: [],
    replyId: null,
    kind: null,
    version: null,
    status: "idle",
    error: null,
    lastSeq: -1,
    seqGap: false,
  };
}

function trackSeq(state: StreamState, seq: number): void {
  if (typeof seq !== "number" || Number.isNaN(seq) || seq < 0) return;
  if (state.lastSeq >= 0 && seq !== state.lastSeq + 1) state.seqGap = true;
  if (seq > state.lastSeq) state.lastSeq = seq;
}

function attach(state: StreamState, node: StreamNode): void {
  state.byId[node.id] = node;
  if (node.parent === null) {
    if (node.tag !== REPLY_TAG) state.rootIds.push(node.id);
    return;
  }
  const parent = state.byId[node.parent];
  if (parent) parent.childIds.push(node.id);
}

export function applyPart(state: StreamState, part: Part): StreamState {
  if (state.status === "idle") state.status = "streaming";
  trackSeq(state, part.seq);

  switch (part.type) {
    case "open": {
      const node: StreamNode = {
        id: part.id,
        tag: part.tag,
        parent: part.parent,
        attrs: part.attrs || {},
        text: "",
        atomic: false,
        childIds: [],
        status: "open",
      };
      attach(state, node);
      if (part.tag === REPLY_TAG) {
        state.replyId = part.id;
        const k = node.attrs.kind;
        state.kind = typeof k === "string" ? k : null;
        if (typeof part.version === "number") state.version = part.version;
      }
      return state;
    }
    case "delta": {
      const node = state.byId[part.id];
      if (node) node.text += part.text;
      return state;
    }
    case "close": {
      const node = state.byId[part.id];
      if (node) node.status = "closed";
      return state;
    }
    case "item": {
      const node: StreamNode = {
        id: part.id,
        tag: part.tag,
        parent: part.parent,
        attrs: part.attrs || {},
        text: "",
        payload: part.payload,
        atomic: true,
        childIds: [],
        status: "closed",
      };
      attach(state, node);
      return state;
    }
    case "error": {
      state.status = "error";
      state.error = {
        message: part.message || part.error || "stream error",
        code: typeof part.code === "number" ? part.code : null,
      };
      return state;
    }
    case "done": {
      if (state.status !== "error") state.status = "done";
      return state;
    }
    default:
      return state;
  }
}

export function reduceParts(parts: Part[]): StreamState {
  const state = initStreamState();
  for (const part of parts) applyPart(state, part);
  return state;
}

export type ShapeKind = "atomic" | "container" | "prose" | "empty";

export function inferNodeKind(node: StreamNode): ShapeKind {
  if (node.atomic) return "atomic";
  if (node.childIds.length > 0) return "container";
  if (node.text.length > 0) return "prose";
  return "empty";
}

export function deriveRenderedText(state: StreamState): string {
  const parts: string[] = [];
  for (const id of state.rootIds) {
    const node = state.byId[id];
    if (node && !node.atomic && node.text.length > 0) parts.push(node.text);
  }
  return parts.join("\n\n");
}
